import { combineReducers, configureStore } from "@reduxjs/toolkit";
import cartReducer from "features/reducers/replacementOrder/cart.ts";
import impersonationSlice from "features/reducers/settings/impersonation.ts";
import submittingReplacementOrderSlice from "features/reducers/replacementOrder/submittingReplacementOrder.ts";
import submittedReplacementOrdersSlice from "features/reducers/replacementOrder/submittedReplacementOrders.ts";
import newOrderSlice from "features/reducers/newOrder/newOrder.ts";
import dialogStateSlice from "features/reducers/dialogState/dialogState.ts";
import truckSlice from "features/reducers/truck/truck";
import { userApiSlice, userEmulationApiSlice } from "features/api/userApi.ts";
import { orderApiSlice } from "features/api/orderApi.ts";
import { imageApiSlice } from "features/api/imageApi.ts";
import { addressApiSlice } from "features/api/addressApi.ts";
import { accountApiSlice } from "features/api/accountApi.ts";
import { newOrderApiSlice } from "features/api/newOrderApi.ts";
import { designerApiSlice } from "features/api/designerApi.ts";
import { write } from "hooks/useSession";
import { globalsApiSlice } from "features/api/globalsApi.ts";
import { configurationsApiSlice } from "features/api/configurationsApi";
import { truckApiSlice } from "features/api/truckApi";
import { customQuoteApiSlice } from "features/api/customQuotesApi";
import { pdfApiSlice } from "features/api/pdfApi.ts";
import { permissionsApiSlice } from "features/api/permissionsApi.ts";
import { preferencesApiSlice } from "features/api/preferencesApi.ts";
import { leadGenApiSlice } from "features/LeadGen/api/leadGenApi.ts";
import { crossSliceInvalidationMiddleware } from "features/api/middleware/crossSliceInvalidationMiddleware.ts";
import { merchandiseProductsApiSlice } from "features/MarketingMaterials/api/merchProductsApi.ts";
import { storesApiSlice } from "features/api/storesApi.ts";
import marketingMaterialsCartReducer from "features/reducers/marketingMaterials/cart.ts";

const rootReducer = combineReducers({
	cart: cartReducer,
	marketingMaterialsCart: marketingMaterialsCartReducer,
	dialogState: dialogStateSlice,
	impersonation: impersonationSlice,
	submittingReplacementOrder: submittingReplacementOrderSlice,
	submittedReplacementOrders: submittedReplacementOrdersSlice,
	newOrder: newOrderSlice,
	truck: truckSlice,
	[orderApiSlice.reducerPath]: orderApiSlice.reducer,
	[userApiSlice.reducerPath]: userApiSlice.reducer,
	[userEmulationApiSlice.reducerPath]: userEmulationApiSlice.reducer,
	[imageApiSlice.reducerPath]: imageApiSlice.reducer,
	[addressApiSlice.reducerPath]: addressApiSlice.reducer,
	[newOrderApiSlice.reducerPath]: newOrderApiSlice.reducer,
	[accountApiSlice.reducerPath]: accountApiSlice.reducer,
	[designerApiSlice.reducerPath]: designerApiSlice.reducer,
	[globalsApiSlice.reducerPath]: globalsApiSlice.reducer,
	[configurationsApiSlice.reducerPath]: configurationsApiSlice.reducer,
	[truckApiSlice.reducerPath]: truckApiSlice.reducer,
	[customQuoteApiSlice.reducerPath]: customQuoteApiSlice.reducer,
	[pdfApiSlice.reducerPath]: pdfApiSlice.reducer,
	[permissionsApiSlice.reducerPath]: permissionsApiSlice.reducer,
	[preferencesApiSlice.reducerPath]: preferencesApiSlice.reducer,
	[leadGenApiSlice.reducerPath]: leadGenApiSlice.reducer,
	[storesApiSlice.reducerPath]: storesApiSlice.reducer,
	[merchandiseProductsApiSlice.reducerPath]: merchandiseProductsApiSlice.reducer
});

export function setupStore(preloadedState?: Partial<RootState>) {
	return configureStore({
		preloadedState,
		reducer: rootReducer,
		devTools: process.env.NODE_ENV !== "prod" && process.env.NODE_ENV !== "production",
		middleware: (getDefaultMiddleware) =>
			getDefaultMiddleware()
				.concat(crossSliceInvalidationMiddleware)
				.concat(orderApiSlice.middleware)
				.concat(userApiSlice.middleware)
				.concat(userEmulationApiSlice.middleware)
				.concat(imageApiSlice.middleware)
				.concat(addressApiSlice.middleware)
				.concat(accountApiSlice.middleware)
				.concat(newOrderApiSlice.middleware)
				.concat(designerApiSlice.middleware)
				.concat(globalsApiSlice.middleware)
				.concat(configurationsApiSlice.middleware)
				.concat(truckApiSlice.middleware)
				.concat(customQuoteApiSlice.middleware)
				.concat(pdfApiSlice.middleware)
				.concat(preferencesApiSlice.middleware)
				.concat(permissionsApiSlice.middleware)
				.concat(leadGenApiSlice.middleware)
				.concat(storesApiSlice.middleware)
				.concat(merchandiseProductsApiSlice.middleware)
	});
}

export const store = setupStore();

store.subscribe(() => {
	const {
		cart,
		marketingMaterialsCart,
		impersonation,
		submittingReplacementOrder,
		submittedReplacementOrders,
		newOrder,
		truck,
		dialogState
	} = store.getState();
	write("_cart", cart);
	write("_marketingMaterialsCart", marketingMaterialsCart);
	write("_impersonation", impersonation);
	write("_submittingReplacementOrder", submittingReplacementOrder);
	write("_submittedReplacementOrders", submittedReplacementOrders);
	write("_newOrder", newOrder);
	write("_dialogState", dialogState);
	write("_truck", truck);
});

export type AppStore = ReturnType<typeof setupStore>;
export type RootState = ReturnType<typeof rootReducer>;
